/**
 * Field Layout CSS Utilities
 *
 * Minimal utilities for mapping backend width values to CSS properties.
 * All grouping logic is handled by the backend.
 */

/**
 * Gets CSS flex value for a field based on its width
 * @param {number|string} width - Width value from backend (0.4-2, 'full')
 * @returns {string} CSS flex value
 */
export const getFieldFlexStyle = (width) => {
  const normalized = normalizeWidth(width);

  if (normalized === 'full') {
    return '1 1 100%';
  }

  return `${normalized} 1 0`;
};

/**
 * Gets CSS class name for a field based on its width
 * @param {number|string} width - Width value from backend
 * @returns {string} CSS class name
 */
export const getFieldWidthClass = (width) => {
  const normalized = normalizeWidth(width);

  if (normalized === 'full') {
    return 'dynamic-ehr-drawer__field--full';
  }

  if (normalized >= 2) {
    return 'dynamic-ehr-drawer__field--wide';
  }

  if (normalized < 1) {
    return 'dynamic-ehr-drawer__field--compact';
  }

  return 'dynamic-ehr-drawer__field--standard';
};

/**
 * Gets CSS class for a row based on its fields
 * @param {Array} fieldsInRow - Array of field objects in the row
 * @returns {string} CSS class name
 */
export const getRowClass = (fieldsInRow = []) => {
  const classes = ['dynamic-ehr-drawer__field-row'];

  // Add equal-width class if all fields have the same width
  if (fieldsInRow.length >= 2) {
    const widths = fieldsInRow.map(f => normalizeWidth(f.width));
    const allEqual = widths.every(w => w === widths[0]);

    if (allEqual) {
      classes.push(`dynamic-ehr-drawer__field-row--equal-${fieldsInRow.length}`);
    }
  }

  return classes.join(' ');
};

/**
 * Normalizes width value to standard format
 * @param {*} width - Can be number, string, or undefined
 * @returns {number|string} Normalized width value
 */
const normalizeWidth = (width) => {
  if (width === undefined || width === null) {
    return 1; // Default width
  }

  if (width === 'full' || width === '100%') {
    return 'full';
  }

  const numWidth = Number(width);
  if (!Number.isNaN(numWidth) && numWidth > 0) {
    return numWidth;
  }

  return 1; // Fallback
};
